import { defineComponent as _defineComponent } from 'vue'
import { type PropType, computed, useAsync, useRoute, useStore } from "@nuxtjs/composition-api";

import { useAmplitude, useConfig, useVuetify } from "@/composables";
import { useConsumerSearchStore } from "@/store";
import { getAmplitudeStore } from "@/store/amplitude";
import { getExperimentsStore } from "@/store/experiments";
import { getFollowerStatisticsStore } from "@/store/follower-statistics";
import { getProfilesStore } from "@/store/profiles";
import { getSearchStore } from "@/store/search";
import { EXPERIMENTS_CONSUMER_SEARCH, FEATURE_FLAG_ACTIVE } from "@/utils/experiments/constants";


export default /*#__PURE__*/_defineComponent({
  __name: 'LtkProfileHeader',
  props: {
  profileId: {
    type: String,
    required: true,
  },
  showFollowers: {
    type: Boolean,
    default: false,
  },
  showFollowBtn: {
    type: Boolean,
    default: true,
  },
  size: {
    type: [String, Number],
    default: 32,
  },
  largeFollowButton: {
    type: Boolean,
    default: false,
  },
  btnFollowPosition: {
    type: String as PropType<"left" | "right">,
    default: "right",
  },
  referringComponent: {
    type: String,
    default: "",
  },
  headerIndex: {
    // Index of this element in the page layout for sending to search analytics
    type: Number,
    default: -1,
  },
},
  setup(__props) {

const props = __props;



const store = useStore();
const amplitude = useAmplitude();
const config = useConfig();
const route = useRoute();
const vuetify = useVuetify();
const experimentsStore = getExperimentsStore(store);
const searchStore = getSearchStore(store);
const amplitudeStore = getAmplitudeStore(store);
const consumerSearchStore = useConsumerSearchStore();

const isConsumerSearchEnabled = computed(
  () => experimentsStore.variant(EXPERIMENTS_CONSUMER_SEARCH).value === FEATURE_FLAG_ACTIVE,
);

const profile = computed(() => {
  const profilesStore = getProfilesStore(store);
  return profilesStore.profiles[props.profileId];
});

const followers = computed(() => {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  }).format(getFollowerStatisticsStore(store).followerStatistics[props.profileId] ?? 0);
});

const isLargeAvatar = computed(() => props.size.toString() === "88");
const isCreatorShop = computed(() => route.value.name === "explore-displayName");
const isSearchPage = computed(() => route.value.name === "search");

const isMobile = computed(() => vuetify.breakpoint.xsOnly);

const fetch = async () => {
  if (isCreatorShop.value) {
    await getFollowerStatisticsStore(store).fetchFollowerStatistics(props.profileId);
  }
};
useAsync(() => fetch(), "Follower Statistics");

function onProfileClick() {
  if (props.referringComponent) {
    amplitudeStore.setReferringComponent(props.referringComponent);
  }
  if (isSearchPage.value) {
    searchAnalytics();
  }
}

function searchAnalytics() {
  const isPostSearch = route.value.query.type.toString() === "post";
  if (!isPostSearch) {
    amplitude?.sendExitEvent("/search/tap", {
      "click-position": props.headerIndex + 1,
      "content-id": props.profileId,
      "content-type": "profile",
      event: "Search Result Click",
      index: `${config.env}-ltk-creator-profiles`,
      "is-redis": isConsumerSearchEnabled.value,
      page: "search-results",
      platform: "web",
      "referring-component": props.referringComponent,
      "search-experience": "matching-creator-search",
      "search-record-id": props.profileId,
      "search-string": route.value.query.keyword.toString(),
      version: config.version,
    });
  }
  amplitude?.sendExitEvent("/ltk-search-summary", {
    "referring-component": props.referringComponent,
    ...(isConsumerSearchEnabled.value
      ? {
          "post-count": consumerSearchStore.ltks.length,
          "profile-count": consumerSearchStore.profiles.length,
        }
      : {
          "post-count": searchStore.searchTypeItem("post").active.total,
          "profile-count": searchStore.searchTypeItem("profile").active.total,
        }),
    query: route.value.query.keyword.toString(),
  });
  // If consumer search is enabled, do not send search insights feedback to Alogolia
  if (isConsumerSearchEnabled) {
    return;
  }
  // Else, send search insights feedback to Algolia
  let position = -1;
  // If they click a Creator header on from the post page, we can't work out which one it is (without counting elements,) so we have to rely on it being passed in
  if (route.value.query.type === "post") {
    position = props.headerIndex;
  } else {
    position = searchStore.searchTypeItem("profile").active.ids.findIndex((id) => id === props.profileId);
  }
  searchStore.resultClick({
    type: route.value.query.type as SearchType,
    event: "Search Result Click",
    objectId: props.profileId,
    position,
    rootReferrer: amplitudeStore.rootReferrer ?? "",
    searchExperience: `Search-${route.value.query.type}`,
  });
}

return { __sfc: true,props, store, amplitude, config, route, vuetify, experimentsStore, searchStore, amplitudeStore, consumerSearchStore, isConsumerSearchEnabled, profile, followers, isLargeAvatar, isCreatorShop, isSearchPage, isMobile, fetch, onProfileClick, searchAnalytics }
}

})